import type {
  IllustrationSpec,
  IllustrationPalette,
  PetIllustrationVariant,
  PetKind,
} from '../types';

/**
 * Deterministic silhouette + palette derived from a pet id. Used as a
 * fallback whenever the backend returns no Firebase photo URL — the same
 * id always renders the same illustration so the page is stable across
 * loads and SSR/hydration.
 *
 * The palette set is hand-curated from the design tokens (concept 06).
 */

const TOK = {
  ink: '#0f2a4a',
  inkSoft: '#1f3c5f',
  paper: '#f3ead8',
  paperWarm: '#fcf6e4',
  paperDeep: '#ebdcc0',
  terracotta: '#c25a3a',
  ochre: '#c68c3b',
  rust: '#a14a2b',
  sage: '#7a8b5f',
} as const;

const DOG_PALETTES: IllustrationPalette[] = [
  { bg: TOK.ochre,  body: TOK.ink,        eyes: TOK.paper, mouth: TOK.paper },
  { bg: TOK.rust,   body: TOK.paper,      eyes: TOK.ink,   mouth: TOK.ink   },
  { bg: TOK.inkSoft, body: TOK.paperDeep, eyes: TOK.ink,   mouth: TOK.ink, accent: TOK.terracotta },
  { bg: TOK.paperDeep, body: TOK.ink,    eyes: TOK.paper, mouth: TOK.paper },
  { bg: TOK.terracotta, body: TOK.paper, eyes: TOK.ink,   mouth: TOK.ink   },
];

const CAT_PALETTES: IllustrationPalette[] = [
  { bg: TOK.sage,        body: TOK.ink,        eyes: TOK.paper, mouth: TOK.paper },
  { bg: TOK.ink,         body: TOK.terracotta, eyes: TOK.paper, mouth: TOK.paper },
  { bg: TOK.ochre,       body: TOK.ink,        eyes: TOK.paper, mouth: TOK.paper },
  { bg: TOK.rust,        body: TOK.paperDeep,  eyes: TOK.ink,   mouth: TOK.ink   },
  { bg: TOK.paperWarm,   body: TOK.ink,        eyes: TOK.terracotta, mouth: TOK.ink },
];

const DOG_VARIANTS: PetIllustrationVariant[] = ['default', 'floppy', 'senior'];
const CAT_VARIANTS: PetIllustrationVariant[] = ['default', 'smile'];

/** Stable 32-bit hash from a string. djb2 — fast, good enough for indexing. */
function hash(input: string): number {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function illustrationFromId(
  id: string,
  kind: PetKind,
  ageHint?: number | null,
): IllustrationSpec {
  const h = hash(id);

  if (kind === 'dog') {
    // Senior pets (≥ 96 months / 8 years) prefer the senior variant for a visual match.
    const variant: PetIllustrationVariant =
      ageHint != null && ageHint >= 96
        ? 'senior'
        : DOG_VARIANTS[h % DOG_VARIANTS.length];
    const palette = DOG_PALETTES[(h >> 3) % DOG_PALETTES.length];
    return { kind, variant, palette };
  }

  // cat
  const variant = CAT_VARIANTS[h % CAT_VARIANTS.length];
  const palette = CAT_PALETTES[(h >> 3) % CAT_PALETTES.length];
  return { kind, variant, palette };
}

export const ILLUSTRATION_TOKENS = TOK;
